import Promise from 'bluebird';
import { getGuid } from 'content-creation-common/utils/guid';
import { removeSpecialCharacters } from 'content-creation-common/utils/string';
import {
  ALREADY_FAST_START_ERR,
  getFileParts as getFilePartsNonStreamable,
} from '../util/qtFastStart';

import {
  ACCOUNT_TYPE,
  DEFAULT_TEMPLATE_CATEGORY,
  DEV,
  ERROR_MAP,
  GROUP_TYPE,
  HOT,
  NONE_TEMPLATE_ID,
  PUBLISHING_TARGET,
  SINGULAR_ACCOUNT_TYPE,
  STOCK_MEDIA_SEARCH_RESULT_AMMOUNT,
  TEST,
  THUMBNAIL_FRAME_NUMBER,
  VALID_CREATIVE_MODES,
  VALID_EDITOR_MODES,
} from '../constants';
import {
  DEFAULT_CREATIVES_LIMIT,
  VALID_CREATIVE_ORIGINS,
} from '../features/creative/constants';
import {
  MEDIA_SOURCES,
  VALID_MEDIA_SOURCES,
} from '../features/media/constants';
import { wrapAPICall } from './util';
import * as AUTH_API from './auth-api';
import {
  getArrayBufferFromFileURI,
  getLocalURIFromFile,
  loadFile,
} from '../util/file';
import { log, warn, error } from '../util/log';
import {
  createEditionForCreative as createEditionForCreativeFixture,
  createZippedCreativeVersion as createZippedCreativeVersionFixture,
  getAccounts as getAccountsFixture,
  getUserFeatureFlags as getUserFeatureFlagsFixture,
  getOrganizations as getOrganizationsFixture,
  getOrganizationPermissions as getOrganizationPermissionsFixture,
  getPublisherPreferences as getPublisherPreferencesFixture,
  getShotsByMediaId as getShotsByMediaIdFixture,
  loadFonts as loadFontsFixture,
  updateRichSnapForCreative as updateRichSnapForCreativeFixture,
} from './creativeSuiteFixturesApi';
import { isEmpty as isEmptyArray } from '../util/array';
import {
  isArray,
  isBoolean,
  isEmptyString,
  isNil,
  isNotEmptyString,
  isNotNil,
} from '../util/object';
import { clamp, isNotNumber, isNumber } from '../util/numbers';
import { VALID_TEMPLATE_TAG_TYPES } from '../features/tags/constants';
import TAG_ERRORS from '../features/tags/errors';
import CreativeContentWireObject from '../model/api/wire-objects/CreativeContentWireObject';
import { appendQueryParams } from '../util/queryString';
import ConcurrentTaskQueue from '../model/tasks/ConcurrentTaskQueue';
import Task from '../model/tasks/Task';
import APIResponse from '../model/api/APIResponse';
import APIWorkerService from './worker/APIWorkerService';
import {
  calculateSpeedKbps,
  IDEAL_PER_PART_UPLOAD_TIME_MAX_SECONDS,
  INITIAL_TEST_PART_SIZE,
  MAX_FILE_SIZE_PER_REQUEST,
  MAX_PARALLEL_UPLOAD_REQUESTS,
} from '../util/network';
import SequentialTaskQueue from '../model/tasks/SequentialTaskQueue';
import { captureException } from '../util/sentry.js';

// Create the worker service instances to interface between these API calls and the worker
const apiWorkerService = new APIWorkerService();
const apiWorkerServiceWithStampedeProtection = new APIWorkerService({
  stampedeProtection: true,
});

export const getMediaList = wrapAPICall(() => {
  return apiWorkerService.get('/api/v1/me/media/partial');
});

export const getMediaById = wrapAPICall((serverId) => {
  if (isNil(serverId)) {
    throw new Error('Cannot load media with empty id');
  }
  return apiWorkerServiceWithStampedeProtection.get(
    `/api/v1/media/${serverId}`,
  );
});

export const getMediaFileUrl = wrapAPICall((serverId) => {
  if (isNil(serverId)) {
    throw new Error('Cannot load media with empty id');
  }

  return apiWorkerServiceWithStampedeProtection.get(
    `/api/v1/media/${serverId}/fileUrl`,
  );
});

export const getArrayBufferFromURL = wrapAPICall((url) => {
  if (isEmptyString(url)) {
    throw new Error('Cannot supply empty URLs');
  }

  // To support local urls
  if (url.startsWith('data:') || url.startsWith('blob:')) {
    return getArrayBufferFromFileURI(url).then((data) => {
      return Promise.resolve(new APIResponse(true, data));
    });
  }

  if (!url.startsWith('/')) {
    throw new Error('Cannot use arbitrary URLs');
  }

  return apiWorkerService.get(url, {
    responseType: 'arraybuffer',
  });
});

export const loadFontById = wrapAPICall((fontId) => {
  if (isNil(fontId)) {
    throw new Error('Cannot load media with empty id');
  }
  return apiWorkerService.get(`/api/v1/fonts2/${fontId}/ttf?_ab=true`, {
    responseType: 'arraybuffer',
  });
});

export const loadFontByName = wrapAPICall((psName) => {
  if (isNil(psName)) {
    throw new Error('Cannot load font with empty name');
  }
  // the _ab is just there for cache busting
  return apiWorkerService.get(`/api/v1/fonts2/byName/${psName}/ttf?_ab=true`, {
    responseType: 'arraybuffer',
  });
});

export const createNewMediaId = wrapAPICall(
  (
    { organizationId, adAccountId, publisherId, businessProfileId, editionId },
    width = undefined,
    height = undefined,
  ) => {
    const mediaObj = {};

    if (isNotNil(organizationId)) {
      mediaObj.organization_id = organizationId;
    }
    if (isNotNil(adAccountId)) {
      mediaObj.ad_account_id = adAccountId;
    }
    if (isNotNil(publisherId)) {
      mediaObj.publisher_id = publisherId;
    }
    if (isNotNil(businessProfileId)) {
      mediaObj.business_profile_id = businessProfileId;
    }
    if (isNotNil(editionId)) {
      mediaObj.edition_id = editionId;
    }
    if (isNotNil(width)) {
      mediaObj.width = width;
    }
    if (isNotNil(height)) {
      mediaObj.height = height;
    }

    return apiWorkerService.post('/api/v1/media', {
      media: [mediaObj],
    });
  },
);

export const findImageCenter = wrapAPICall((mediaId) => {
  const formData = new FormData();
  formData.append('id', mediaId);

  return apiWorkerService.post('/api/v1/media/findImageCenterV2', formData);
});

export const save = wrapAPICall(
  ({
    creative,
    creativeId,
    creativeMode,
    creativeName,
    creativeOrigin,
    creativeTemplateId,
    currentUserContext: {
      adAccountId,
      editionId,
      organizationId,
      publisherId,
      businessProfileId,
    },
    editorMode,
  }) => {
    const name = creativeName || `creative-${getGuid()}`;
    const creativeData = {
      name,
      data: JSON.stringify(creative),
    };

    if (isNotNil(creativeTemplateId)) {
      // If there is a template id, use it.
      creativeData.template_id = creativeTemplateId;
    } else if (isNil(creativeId)) {
      // If there is no template id and the creative is new (no id) set "none";
      creativeData.template_id = NONE_TEMPLATE_ID;
    }

    if (isNotNil(organizationId)) {
      creativeData.organization_id = organizationId;
    }

    if (isNotNil(adAccountId)) {
      creativeData.ad_account_id = adAccountId;
    }

    if (isNotNil(publisherId)) {
      creativeData.publisher_id = publisherId;
    }

    if (isNotNil(businessProfileId)) {
      creativeData.business_profile_id = businessProfileId;
    }

    if (isNotNil(editionId)) {
      creativeData.edition_id = editionId;
    }

    if (isNotNil(creativeMode) && VALID_CREATIVE_MODES.has(creativeMode)) {
      creativeData.creative_mode = creativeMode;
    }

    if (
      isNotNil(creativeOrigin) &&
      VALID_CREATIVE_ORIGINS.has(creativeOrigin)
    ) {
      creativeData.creative_origin = creativeOrigin;
    }

    if (isNotNil(editorMode) && VALID_EDITOR_MODES.has(editorMode)) {
      creativeData.editor_mode = editorMode;
    }

    // Update existing creative
    if (isNotNil(creativeId)) {
      creativeData.id = creativeId;
      return apiWorkerService.put('/api/v1/creatives/', {
        creatives: [creativeData],
      });
    }

    // Create a new creative
    return apiWorkerService.post('/api/v1/creatives/', {
      creatives: [creativeData],
    });
  },
);

export const load = wrapAPICall(
  (creativeId, creativeVersionId, noCreativeJson) => {
    if (!creativeId) {
      throw new Error('Invalid creative id given.');
    }

    let url = `/api/v1/creatives/${creativeId}`;
    if (creativeVersionId) {
      url += `/versions/${creativeVersionId}`;
    }
    if (noCreativeJson) {
      if (isNil(creativeVersionId)) {
        throw new Error(
          'The noCreativeJson option is not supported without creativeVersionId.',
        );
      }
      // exclude returning the creativeDocument data
      url = appendQueryParams(url, { noCreativeJson: true });
    }
    return apiWorkerService.get(url);
  },
);

export const publish = wrapAPICall(
  (publishingTarget, creativeId, versionId) => {
    if (!creativeId || !versionId) {
      throw new Error('Invalid creative parameters given.');
    }
    const publishEndpoint =
      publishingTarget === PUBLISHING_TARGET.ADS
        ? 'publish'
        : 'discover_publish';
    return apiWorkerService.post(
      `/api/v1/creatives/${creativeId}/versions/${versionId}/${publishEndpoint}`,
    );
  },
);

export const publishPhoto = wrapAPICall(
  (publishingTarget, creativeId, versionId, frame = 0) => {
    if (!creativeId || !versionId || isNotNumber(frame) || frame < 0) {
      throw new Error('Invalid creative parameters given.');
    }

    // If a publisher (discover user) tries to publish a photo ad, just use the discover publish endpoint for now.
    if (publishingTarget === PUBLISHING_TARGET.DISCOVER) {
      return publish(publishingTarget, creativeId, versionId);
    }

    // /{creative_id}/versions/{version_id}/publish-frame/{frame_number}
    return apiWorkerService.post(
      `/api/v1/creatives/${creativeId}/versions/${versionId}/publish-frame/${frame}`,
    );
  },
);

export const publishParallel = wrapAPICall(
  (publishingTarget, creativeId, versionId) => {
    if (!creativeId || !versionId) {
      throw new Error('Invalid creative parameters given.');
    }
    if (publishingTarget !== PUBLISHING_TARGET.DISCOVER) {
      throw new Error('Publishing target is not supported.');
    }
    return apiWorkerService.post(
      `/api/v1/creatives/${creativeId}/versions/${versionId}/discover_publish_parallel`,
    );
  },
);

export const getCreativeVersions = wrapAPICall((creativeId) => {
  if (!creativeId) {
    throw new Error('Invalid creative id given.');
  }

  return apiWorkerService.get(`/api/v1/creatives/${creativeId}/versions`);
});

export const getUserCreativeListByAccount = wrapAPICall((
  /* SINGULAR_ACCOUNT_TYPE */ accountType,
  accountId,
  limit = DEFAULT_CREATIVES_LIMIT,
) => {
  if (
    accountType !== SINGULAR_ACCOUNT_TYPE.AD_ACCOUNT &&
    accountType !== SINGULAR_ACCOUNT_TYPE.PUBLISHER &&
    accountType !== SINGULAR_ACCOUNT_TYPE.SNAP_PRO
  ) {
    throw Error(
      `Invalid group type provided, must be adaccounts, snap pro or publishers: ${accountType}`,
    );
  }
  if (isNil(accountId)) {
    throw Error(`Account ID is nil, cannot load creatives ${accountId}`);
  }
  return apiWorkerService.get(
    `/api/v1/me/creatives/${accountType}/${accountId}?limit=${limit}`,
  );
});

export const getAccountCreatives = wrapAPICall((
  /* ACCOUNT_TYPE */ accountType,
  accountId,
  withData = false,
) => {
  if (
    accountType !== ACCOUNT_TYPE.AD_ACCOUNT &&
    accountType !== ACCOUNT_TYPE.PUBLISHER &&
    accountType !== ACCOUNT_TYPE.SNAP_PRO
  ) {
    throw Error(
      `Invalid group type provided, must be adaccounts, snap pro or publishers: ${accountType}`,
    );
  }
  if (isNil(accountId)) {
    throw Error(`Account ID is nil, cannot load creatives ${accountId}`);
  }
  if (!isBoolean(withData)) {
    throw Error('Invalid prop provided for withData');
  }

  const basicPath = `/api/v1/${accountType}/${accountId}/creatives`;
  const getPath = withData ? `${basicPath}?withData=true` : basicPath;
  return apiWorkerService.get(getPath);
});

export const deleteCreative = wrapAPICall((creativeId) => {
  if (isNil(creativeId)) {
    throw new Error('Cannot delete creative with empty id');
  }
  return apiWorkerService.delete('/api/v1/creatives', {
    params: {
      ids: creativeId,
    },
  });
});

const buildVideoPiecesWithEndpoint = ({
  buildEndpoint,
  creativeId,
  version,
  outputRanges,
  isSingleFrameSnap,
  isShadowMode,
  editionId,
  snapId,
  shouldPublish,
} = {}) => {
  if (isNil(creativeId) || isNil(version)) {
    throw new Error('Build video API needs creative ID and version');
  }

  const requestData = {};
  // outputRanges could be a list of Intervals OR a lit of plain objects. We want to convert to plain object
  // TODO(lemieux) figure out why toJSON on Intervals isn't working here.
  if (isNotNil(outputRanges)) {
    const objectifiedRanges = outputRanges.map((range) => {
      const { end, start } = range;
      return { end, start };
    });
    requestData.videoPieces = objectifiedRanges;
  }

  const params = {
    editionId,
    isShadowMode,
    isSingleFrameSnap,
    snapId,
    shouldPublish,
  };
  const url = appendQueryParams(
    `/api/v1/creatives/${creativeId}/versions/${version}/${buildEndpoint}`,
    params,
  );
  return apiWorkerService.post(url, requestData);
};

export const buildVideoWithNodeBuilder = wrapAPICall(
  ({
    creativeId,
    editionId,
    isShadowMode = false,
    isSingleFrameSnap = false,
    outputRanges,
    shouldPublish,
    snapId,
    version,
  } = {}) => {
    return buildVideoPiecesWithEndpoint({
      buildEndpoint: 'build-with-node-builder',
      creativeId,
      editionId,
      isShadowMode,
      isSingleFrameSnap,
      outputRanges,
      shouldPublish,
      snapId,
      version,
    });
  },
);

// Use for creating thumbnails and downloading single frames where publishing is not needed, otherwise use build-parallel
export const buildFrameWithNodeBuilder = wrapAPICall(
  (
    creativeId,
    version,
    frameNumber = THUMBNAIL_FRAME_NUMBER,
    isThumbnail = true,
  ) => {
    if (isNil(creativeId) || isNil(version) || isNil(frameNumber)) {
      throw new Error(
        'Build Frame API needs creative ID and version and frameNumber',
      );
    }

    return apiWorkerService.post(
      `/api/v1/creatives/${creativeId}/versions/${version}/build-with-node-builder/${frameNumber}?isThumbnail=${isThumbnail}`,
      {},
    );
  },
);

/**
 * Split a file in to parts for upload
 * @param dataArray
 * @param {Number} maxFileSize
 * @param {Number} initialFileSize
 * @returns {[]}
 */
const splitFile = (
  dataArray,
  maxFileSize = MAX_FILE_SIZE_PER_REQUEST,
  initialFileSize = MAX_FILE_SIZE_PER_REQUEST,
) => {
  const blobs = [];
  let initialFileOffset = 0;

  // If set, add the first part
  if (isNumber(initialFileSize)) {
    initialFileOffset = initialFileSize;
    blobs.push(new Blob([dataArray.subarray(0, initialFileSize)]));
  }

  // Go through subsequent parts and add to array
  for (let i = initialFileOffset; i < dataArray.length; i += maxFileSize) {
    blobs.push(new Blob([dataArray.subarray(i, i + maxFileSize)]));
  }
  return blobs;
};

const getFileParts = (
  file,
  maxFileSize = MAX_FILE_SIZE_PER_REQUEST,
  initialFileSize = MAX_FILE_SIZE_PER_REQUEST,
) => {
  return new Promise((resolve, reject) => {
    loadFile(file)
      .then((bytes) => {
        const fileParts = splitFile(bytes, maxFileSize, initialFileSize);
        resolve({
          fileParts,
          fileSize: bytes.length,
        });
      })
      .catch((error) => {
        reject(error);
      });
  });
};

const uploadScrapedMedia = (serverId, scrapedMedia, jobId, hash) => {
  const { URL, fileName, mimeType } = scrapedMedia;

  const formData = new FormData();
  formData.append('url', URL);
  formData.append('file_name', removeSpecialCharacters(fileName));
  formData.append('mime_type', mimeType);
  if (isNotEmptyString(jobId)) {
    formData.append('mediaImportId', jobId);
  }
  formData.append('hash', hash);

  return apiWorkerService.post(
    `/api/v1/media/${serverId}/scraper-upload`,
    formData,
  );
};

export const uploadScrapedMediaFile = wrapAPICall(
  (scrapedMedia, jobId, hash, currentUserContext) => {
    // TODO: progress handler
    let serverId;

    return new Promise((resolve, reject) => {
      createNewMediaId(currentUserContext)
        .then(({ data }) => {
          serverId = data && data.length && data[0].id;
          if (!serverId) {
            return reject(new Error(ERROR_MAP.UPLOAD_ERROR.name));
          }

          return uploadScrapedMedia(serverId, scrapedMedia, jobId, hash);
        })
        .then(() => {
          resolve({ data: { serverId } });
        })
        .catch((error) => {
          return reject(error);
        });
    });
  },
);

const uploadStockMedia = wrapAPICall((serverId, assetId, url, signature) => {
  const formData = new FormData();
  formData.append('assetId', assetId);
  formData.append('downloadUrl', url);
  formData.append('signature', signature);

  return apiWorkerService.post(
    `/api/v1/stockMedia/${serverId}/stock-image-upload`,
    formData,
  );
});

export const uploadStockMediaItem = wrapAPICall(
  (serverId, assetId, url, signature, currentUserContext) => {
    // TODO: progress handler
    return new Promise((resolve, reject) => {
      createNewMediaId(currentUserContext) // Create the server id - the server calls the server id the media id
        .then(({ data }) => {
          serverId = data && data.length && data[0].id;
          if (!serverId) {
            return reject(new Error(ERROR_MAP.UPLOAD_ERROR.name));
          }

          return uploadStockMedia(serverId, assetId, url, signature);
        })
        .then((response, error) => {
          if (error || !response.data) {
            return reject(new Error(ERROR_MAP.UPLOAD_ERROR.name));
          }
          return resolve({
            data: {
              serverId,
              height: response.data.height,
              width: response.data.width,
            },
          });
        });
    });
  },
);

const uploadMedia = (serverId, file, mediaSource, onUploadProgress) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('mime_type', file.type);
  formData.append('file_size', file.size);

  if (isNotNil(mediaSource) && VALID_MEDIA_SOURCES.has(mediaSource)) {
    formData.append('source', mediaSource);
  }

  const responsePromise = apiWorkerService.post(
    `/api/v1/media/${serverId}/uploadV2`,
    formData,
  );

  // Set progress to 1
  // TODO(lemieux) unhack this - get data from worker
  responsePromise.then(() => onUploadProgress(1));
  return responsePromise;
};

export const getShotsByMediaId = DEV
  ? getShotsByMediaIdFixture
  : wrapAPICall((serverId) => {
      return apiWorkerService.get(`/api/v1/media/${serverId}/shots`);
    });

export const importDiscoverAsset = wrapAPICall(
  (assetId, currentUserContext) => {
    let serverId;

    return new Promise((resolve, reject) => {
      createNewMediaId(currentUserContext)
        .then(({ data }) => {
          serverId = data && data.length && data[0].id;
          if (!serverId) {
            return reject(new Error(ERROR_MAP.UPLOAD_ERROR.name));
          }
          return apiWorkerService.post(
            `/api/v1/media/${serverId}/import/discover/${assetId}`,
          );
        })
        .then((response, error) => {
          resolve({ data: { serverId } });
        })
        .catch(() => {
          reject(new Error(ERROR_MAP.UPLOAD_ERROR.name));
        });
    });
  },
);

export const uploadSinglePartFile = wrapAPICall(
  (
    file,
    mediaSource,
    progressHandler,
    currentUserContext,
    width,
    height,
    serverId,
  ) => {
    return new Promise((resolve, reject) => {
      const initialStep = isNil(serverId)
        ? createNewMediaId(currentUserContext, width, height).then(
            ({ data }) => {
              serverId = data && data.length && data[0].id;

              if (!serverId) {
                return reject(new Error(ERROR_MAP.UPLOAD_ERROR.name));
              }
              return serverId;
            },
          )
        : Promise.resolve(serverId);

      initialStep
        .then((serverId) => {
          return uploadMedia(serverId, file, mediaSource, progressHandler);
        })
        .then(({ data }) => {
          resolve({
            data: {
              mediaSource,
              serverId,
              processors: data.processors,
            },
          });
        })
        .catch((error) => {
          return reject(error);
        });
    });
  },
);

// fileSize: the total file size in bytes (cannot exceed 3GB)
const multipartUploadStart = (
  serverId,
  fileName,
  fileSize,
  mimeType,
  numFileParts,
  mediaSource,
) => {
  const data = new FormData();
  data.append('file_name', removeSpecialCharacters(fileName));
  data.append('file_size', fileSize);
  data.append('mime_type', mimeType);
  data.append('number_of_parts', numFileParts);

  if (isNotNil(mediaSource) && VALID_MEDIA_SOURCES.has(mediaSource)) {
    data.append('source', mediaSource);
  }

  return apiWorkerService.post(
    `/api/v1/media/${serverId}/multipart-upload?action=INIT`,
    data,
    { enforceRetry: true },
  );
};

// file is the file part, upload id is returned from multipartUploadStart call
const multipartUploadSendPart = (
  serverId,
  file,
  partNum,
  uploadId,
  mediaSource,
) => {
  const data = new FormData();
  data.append('file', file);
  data.append('part_number', partNum + 1); // api expects the first part to be numbered 1
  data.append('upload_id', uploadId);

  if (isNotNil(mediaSource) && VALID_MEDIA_SOURCES.has(mediaSource)) {
    data.append('source', mediaSource);
  }

  return apiWorkerService.post(
    `/api/v1/media/${serverId}/multipart-upload?action=ADD`,
    data,
    {
      enforceRetry: true,
    },
  );
};

// upload id is returned from multipartUploadStart call
const multipartUploadFinish = (
  serverId,
  fileName,
  fileSize,
  uploadId,
  mediaSource,
) => {
  const data = new FormData();
  data.append('upload_id', uploadId);
  data.append('file_name', removeSpecialCharacters(fileName));
  data.append('file_size', fileSize);

  if (isNotNil(mediaSource) && VALID_MEDIA_SOURCES.has(mediaSource)) {
    data.append('source', mediaSource);
  }

  return apiWorkerService.post(
    `/api/v1/media/${serverId}/multipart-upload?action=FINALIZE`,
    data,
    { enforceRetry: true },
  );
};

const getMultipartUploadTask = (
  serverId,
  filePart,
  partIndex,
  uploadId,
  onProgress,
  mediaSource,
) => {
  return new Task(() => {
    const start = Date.now();
    const responsePromise = multipartUploadSendPart(
      serverId,
      filePart,
      partIndex,
      uploadId,
      mediaSource,
    );

    return responsePromise.then(() => {
      // Set progress for this part to 1.
      // TODO(lemieux) unhack this - use data from worker
      onProgress(1);

      // Return the time this upload chunk took
      const end = Date.now();
      return end - start;
    });
  });
};

export const uploadMultipartFile = wrapAPICall(
  (
    file,
    mediaSource,
    progressHandler,
    currentUserContext,
    width,
    height,
    serverId,
  ) => {
    // Variables for multiple tasks
    // TODO figure out how to not need these

    // Upload ID from media API - used to combine parts of file
    let uploadId;

    // Array of all file parts we need to upload
    let allFileParts;

    // Count of all file parts (allFileParts.length)
    let numFileParts = 0;

    // List of progress percentages that corresponds to similarly indexed items in allFileParts
    const loadedPercents = [];

    // Timing
    const startTime = Date.now();

    // Logs
    const logs = []; // todo: remove profiling code

    // Progress handler creating function
    const getPartProgressHandler = (partIndex) => {
      return (loadedPercent) => {
        loadedPercents[partIndex] = loadedPercent;
        const totalUpload = loadedPercents.reduce((acc, val) => {
          return acc + val;
        }, 0);
        const totalUploadPercent = totalUpload / numFileParts;
        logs.push(
          `${`[${
            Date.now() - startTime
          }] total uploaded percent: `}${totalUploadPercent}`,
        );
        progressHandler(totalUploadPercent);
      };
    };

    // Create the sequential task queue to handle this sequence
    const sequentialTaskQueue = new SequentialTaskQueue();

    // If there is no serverId, lets create a new one.
    if (isNil(serverId)) {
      const createMediaTask = new Task(() => {
        logs.push(`[${Date.now() - startTime}] creating media id...`);
        return createNewMediaId(currentUserContext, width, height).then(
          ({ data }) => {
            logs.push(`[${Date.now() - startTime}] got media id`);
            serverId = data && data.length && data[0].id;
            if (!serverId) {
              throw new Error(ERROR_MAP.UPLOAD_ERROR.name);
            }

            return serverId;
          },
        );
      });

      // Create new media entity;
      sequentialTaskQueue.addTask(createMediaTask);
    }

    // Get file parts, including a smaller initial part.
    const getFilePartsTask = new Task(async () => {
      logs.push(`[${Date.now() - startTime}] breaking file into parts...`);
      if (mediaSource === MEDIA_SOURCES.EPISODE_CREATION_UPLOAD) {
        try {
          return await getFilePartsNonStreamable(
            file,
            MAX_FILE_SIZE_PER_REQUEST,
            Math.floor(INITIAL_TEST_PART_SIZE),
          );
        } catch (e) {
          if (e.message !== ALREADY_FAST_START_ERR) {
            error('Problem getting file parts for episode creation', e);
            captureException(
              e,
              { currentUserContext, filesize: file.size, mediaSource },
              null,
              ['Error attempting to convert uploaded video to streamable'],
            );
          }

          return getFileParts(
            file,
            MAX_FILE_SIZE_PER_REQUEST,
            Math.floor(INITIAL_TEST_PART_SIZE),
          );
        }
      }

      return getFileParts(
        file,
        MAX_FILE_SIZE_PER_REQUEST,
        Math.floor(INITIAL_TEST_PART_SIZE),
      );
    });
    sequentialTaskQueue.addTask(getFilePartsTask);

    // Start the multi-part upload job, does not upload any data yet.
    const initializeMultipartUploadTask = new Task(
      ({ fileParts, fileSize }) => {
        logs.push(`[${Date.now() - startTime}] got file parts`);

        // Assign to variable here for use later
        // TODO(lemieux) unwind this so it can be chained in task queue
        allFileParts = fileParts;
        numFileParts = allFileParts.length;

        logs.push(`[${Date.now() - startTime}] starting multipart upload...`);
        return multipartUploadStart(
          serverId,
          file.name,
          fileSize,
          file.type,
          fileParts.length,
          mediaSource,
        );
      },
    );
    sequentialTaskQueue.addTask(initializeMultipartUploadTask);

    // Upload the first file part, measure the upload speed, and then set the
    // concurrent part count based on the speed.
    const uploadFirstFilePart = new Task(({ data }) => {
      logs.push(`[${Date.now() - startTime}] started multipart upload`);
      uploadId = data && data.upload_id;

      if (!uploadId) {
        throw new Error(ERROR_MAP.UPLOAD_ERROR.name);
      }

      loadedPercents.push(0);
      const partIndex = 0;
      const firstFilePart = allFileParts[partIndex];
      const uploadTask = getMultipartUploadTask(
        serverId,
        firstFilePart,
        partIndex,
        uploadId,
        getPartProgressHandler(partIndex),
        mediaSource,
      );

      return uploadTask
        .run()
        .then((totalTime) => {
          logs.push(
            `[${
              Date.now() - startTime
            }] it took ~${totalTime} to upload first part`,
          );

          // Determine relative network speed
          // Assumes MAX_FILE_SIZE_PER_REQUEST is around 2mb
          // Assumes MAX_PARALLEL_UPLOAD_REQUESTS is 8
          // 8 * 2mb = 16mb
          // To upload 16mb in 120 seconds (2 minutes) you need ~137kbps
          // To upload 8mb in 120 seconds you need ~70kbps
          // To upload 4ms in 120 seconds you need ~35kbps
          // To upload 2mb in 120 seconds you need ~18kbps
          const idealUploadSpeed =
            (MAX_PARALLEL_UPLOAD_REQUESTS * MAX_FILE_SIZE_PER_REQUEST) /
            1024 /
            IDEAL_PER_PART_UPLOAD_TIME_MAX_SECONDS;
          const kbytesPerSecond = calculateSpeedKbps(
            totalTime,
            INITIAL_TEST_PART_SIZE,
          );
          const percentageUploadSpeed = kbytesPerSecond / idealUploadSpeed;
          return clamp(
            Math.ceil(MAX_PARALLEL_UPLOAD_REQUESTS * percentageUploadSpeed),
            1,
            MAX_PARALLEL_UPLOAD_REQUESTS,
          );
        })
        .catch((error) => {
          throw error;
        });
    });
    sequentialTaskQueue.addTask(uploadFirstFilePart);

    // Upload the remaining parts
    const uploadRemainingPartsTask = new Task((concurrentParts) => {
      const concurrentUploadTaskQueue = new ConcurrentTaskQueue(
        concurrentParts,
      );

      // Start at 1 because we already uploaded the first part
      for (let i = 1; i < numFileParts; ++i) {
        loadedPercents.push(0);
        const partIndex = i;
        const filePart = allFileParts[partIndex];
        const uploadTask = getMultipartUploadTask(
          serverId,
          filePart,
          partIndex,
          uploadId,
          getPartProgressHandler(partIndex),
          mediaSource,
        );

        concurrentUploadTaskQueue.addTask(uploadTask);
      }

      return concurrentUploadTaskQueue.run();
    });
    sequentialTaskQueue.addTask(uploadRemainingPartsTask);

    // Finalize the multipart upload
    const finishMultipartUploadTask = new Task(() => {
      logs.push(`[${Date.now() - startTime}] uploaded all file parts`);
      logs.push(`[${Date.now() - startTime}] finishing multipart upload..`);
      return multipartUploadFinish(
        serverId,
        file.name,
        file.size,
        uploadId,
        mediaSource,
      );
    });
    sequentialTaskQueue.addTask(finishMultipartUploadTask);

    // Eventually return the the mediaSource and serverId
    return sequentialTaskQueue
      .run()
      .then(({ data }) => {
        logs.push(`[${Date.now() - startTime}] done`);
        log(logs.join('\n'));

        return {
          data: {
            mediaSource,
            serverId,
            processors: data.processors,
          },
        };
      })
      .catch((error) => {
        throw error;
      });
  },
);

export const deleteMedia = wrapAPICall((mediaIds) => {
  const idList = mediaIds.join(',');
  return apiWorkerService.delete(`/api/v1/media/${idList}`);
});

export const loadFonts = DEV
  ? loadFontsFixture
  : wrapAPICall((groupType, groupId) => {
      if (
        groupType !== GROUP_TYPE.PUBLISHERS &&
        groupType !== GROUP_TYPE.ORGANIZATIONS &&
        groupType !== GROUP_TYPE.SNAP_PRO
      ) {
        throw Error(
          `Invalid group type provided, must be organizations or p: ${groupType}`,
        );
      }
      return apiWorkerService.get(`/api/v1/${groupType}/${groupId}/fonts`);
    });

export const scrapeUrl = wrapAPICall((url) => {
  const formData = new FormData();
  formData.append('url', url);
  return apiWorkerService.post('/api/v1/media/import', formData);
});

export const pollForScraperOutput = wrapAPICall((scrapeJobId) => {
  return apiWorkerService.get(`/api/v1/media/import/${scrapeJobId}`);
});

export const stockMediaImages = wrapAPICall(
  (searchQuery, page, pageSize = STOCK_MEDIA_SEARCH_RESULT_AMMOUNT) => {
    const params = {
      pageSize,
      pageNumber: page,
      phrase: searchQuery,
    };

    return apiWorkerService.get('/api/v1/stockMedia/search/images', { params });
  },
);

export const stockMediaVideos = wrapAPICall(
  (searchQuery, page, pageSize = STOCK_MEDIA_SEARCH_RESULT_AMMOUNT) => {
    const params = {
      pageSize,
      pageNumber: page,
      phrase: searchQuery,
    };

    return apiWorkerService.get('/api/v1/stockMedia/search/videos', { params });
  },
);

export const getGroupTemplates = wrapAPICall((
  /* GROUP_TYPE */ groupType,
  groupId,
  includeInactive,
) => {
  if (
    groupType !== GROUP_TYPE.ORGANIZATIONS &&
    groupType !== GROUP_TYPE.PUBLISHERS &&
    groupType !== GROUP_TYPE.SNAP_PRO
  ) {
    throw Error(
      `Invalid group type provided, must be organizations, snap pro account or publishers: ${groupType}`,
    );
  }

  const params = {};
  if (includeInactive) {
    params.active = 'false';
  }

  return apiWorkerService.get(`/api/v1/${groupType}/${groupId}/templates`, {
    params,
  });
});

export const getTemplateById = wrapAPICall((serverId) => {
  if (isNil(serverId)) {
    throw new Error('Cannot load template with empty id.');
  }
  return apiWorkerService.get(`/api/v1/templates/${serverId}`);
});

export const saveTemplate = wrapAPICall(
  (
    template,
    {
      active: templateActive,
      category: templateCategory,
      description: templateDescription,
      id: templateId,
      name: templateName,
      ordinal: templateOrdinal,
      thumbnailFrameNumber: templateThumbnailFrameNumber,
    },
    { organizationId, adAccountId, publisherId, businessProfileId },
  ) => {
    const name = templateName || `template-${getGuid()}`;
    const description = templateDescription || '';
    const category =
      templateCategory || DEFAULT_TEMPLATE_CATEGORY.defaultMessage;
    const ordinal = !isNaN(parseInt(templateOrdinal, 10))
      ? parseInt(templateOrdinal, 10)
      : 0;
    const thumbnailFrameNumberParsed = parseInt(
      templateThumbnailFrameNumber,
      10,
    );
    const thumbnailFrameNumber = !isNaN(thumbnailFrameNumberParsed)
      ? thumbnailFrameNumberParsed
      : null;
    const active = templateActive || false;
    const templateData = {
      active,
      category,
      description,
      name,
      ordinal,
      thumbnail_frame_number: thumbnailFrameNumber,

      data: JSON.stringify(template),
    };

    // Add user context.
    if (organizationId) {
      templateData.organization_id = organizationId;
    }
    // FIXME(lemieux): Temporary removal of account id on templates. When we have account-level templates re-enable this.
    // if (adAccountId) {
    //   templateData.ad_account_id = adAccountId;
    // }
    if (publisherId) {
      templateData.publisher_id = publisherId;
    }

    if (isNotNil(businessProfileId)) {
      templateData.business_profile_id = businessProfileId;
    }

    // if id, update via put
    if (templateId) {
      templateData.id = templateId;
      return apiWorkerService.put('/api/v1/templates/', {
        templates: [templateData],
      });
    }

    return apiWorkerService.post('/api/v1/templates/', {
      templates: [templateData],
    });
  },
);

export const deleteTemplate = wrapAPICall((templateId) => {
  if (isNil(templateId)) {
    throw new Error('Cannot delete template with empty ID.');
  }

  return apiWorkerService.delete('/api/v1/templates', {
    params: {
      ids: templateId,
    },
  });
});

export const getUser = wrapAPICall(() => {
  return apiWorkerService.get('/api/v1/me');
});

export const getPermissions = wrapAPICall(() => {
  return apiWorkerService.get('/api/v1/me/permissions');
});

/*
 FIXME: "GLOBAL" permissions could also exist on the publisher level,
 but for now we only care about the organization level
 */
export const getGlobalPermissions = wrapAPICall(() => {
  return apiWorkerService.get('/api/v1/me/permissions/organization/GLOBAL');
});

export const getOrganizationPermissions = HOT
  ? getOrganizationPermissionsFixture
  : wrapAPICall((organizationId) => {
      if (isNil(organizationId)) {
        throw new Error('Cannot load organization permissions with empty id.');
      }

      return apiWorkerService.get(
        `/api/v1/me/permissions/organization/${organizationId}`,
      );
    });

export const getAccountPermissions = wrapAPICall((accountId) => {
  if (isNil(accountId)) {
    throw new Error('Cannot load account permissions with empty id.');
  }

  return apiWorkerService.get(`/api/v1/me/permissions/adaccount/${accountId}`);
});

export const getPublisherPermissions = wrapAPICall((publisherId) => {
  if (isNil(publisherId)) {
    throw new Error('Cannot load publisher permissions with empty id.');
  }

  return apiWorkerService.get(
    `/api/v1/me/permissions/publisher/${publisherId}`,
  );
});

export const getPublishers = wrapAPICall(() => {
  return apiWorkerService.get('/api/v1/me/publishers');
});

export const getDiscoverPublisher = wrapAPICall((publisherId) => {
  if (isNil(publisherId)) {
    throw new Error('Cannot load publisher with empty id.');
  }
  return apiWorkerService.get(`/api/v1/me/publisher/${publisherId}`);
});

export const getDiscoverPublisherStories = wrapAPICall((publisherId) => {
  if (isNil(publisherId)) {
    throw new Error('Cannot load publisher stories with empty id.');
  }
  return apiWorkerService.get(`/api/v1/publishers/${publisherId}/stories`);
});

export const getSnapProPermissions = wrapAPICall((businessProfileId) => {
  if (isNil(businessProfileId)) {
    throw new Error('Cannot load snap pro profile permissions with empty id.');
  }

  return apiWorkerService.get(
    `/api/v1/me/permissions/snap-pro-profile/${businessProfileId}`,
  );
});

export const getSnapProProfiles = wrapAPICall(() => {
  return apiWorkerService.get('/api/v1/me/snap-pro-profiles');
});

// The following two APIs will always return fixtures when using the hot dev
// server to workaround an issue in which the adaccounts request will fail to
// proxy. (The root cause seems to have something to do with the fact that
// that response has the header transfer-encoding: chunked.)
export const getOrganizations = HOT
  ? getOrganizationsFixture
  : wrapAPICall(() => {
      return apiWorkerService.get('/api/v1/me/organizations');
    });

export const getAccounts = HOT
  ? getAccountsFixture
  : wrapAPICall((organizationId) => {
      if (isNil(organizationId)) {
        throw new Error(
          'Cannot load accounts from an organization with empty id.',
        );
      }
      return apiWorkerService.get(
        `/api/v1/organizations/${organizationId}/adaccounts/`,
      );
    });

export const getSSOTicket = wrapAPICall(() => {
  return AUTH_API.getSSOTicket();
});

export const setSSOTicket = wrapAPICall((ticket) => {
  return AUTH_API.setSSOTicket(ticket);
});

export const logout = wrapAPICall(() => {
  return Promise.all([AUTH_API.logout(), AUTH_API.logoutSSO()]);
});

/* Set and Delete User Preferences. Preferences are readable via getUser */
export const setUserPreference = wrapAPICall((name, value) => {
  if (isNil(name)) {
    throw new Error('Cannot save preference with empty name');
  }

  return apiWorkerService.post(
    `/api/v1/me/preferences/${encodeURIComponent(name)}`,
    value,
    {
      headers: {
        'Content-Type': 'text/plain',
      },
    },
  );
});

export const deleteUserPreference = wrapAPICall((name) => {
  if (isNil(name)) {
    throw new Error('Cannot delete preference with empty name');
  }

  return apiWorkerService.delete(
    `/api/v1/me/preferences/${encodeURIComponent(name)}`,
  );
});

export const getPublisherPreferences =
  DEV || TEST
    ? getPublisherPreferencesFixture
    : wrapAPICall((publisherId) => {
        return apiWorkerService.get(
          `/api/v1/publishers/${publisherId}/accountPreferences`,
        );
      });

export const getOrganizationPreferences = wrapAPICall((orgId) => {
  return apiWorkerService.get(
    `/api/v1/organizations/${orgId}/accountPreferences`,
  );
});

export const getSnapProPreferences = wrapAPICall((businessProfileId) => {
  return apiWorkerService.get(
    `/api/v1/snap-pro-profiles/${businessProfileId}/accountPreferences`,
  );
});

export const getAdFeatureGatesByAccountType = wrapAPICall(
  (accountType, accountId) => {
    if (
      !(
        accountType === ACCOUNT_TYPE.ORGANIZATION ||
        accountType === ACCOUNT_TYPE.AD_ACCOUNT
      )
    ) {
      throw Error(
        `Invalid group type provided, must be organizations or adaccounts: ${accountType}`,
      );
    }
    if (isNil(accountId)) {
      throw Error(`Account ID is nil, cannot load feature gates ${accountId}`);
    }

    return apiWorkerService.get(
      `/api/v1/${accountType}/${accountId}/ads-feature-gates`,
    );
  },
);

export const getTags = wrapAPICall(() => {
  return apiWorkerService.get('/api/v1/tags');
});

export const saveTags = wrapAPICall((currentUserContext, tags) => {
  if (isEmptyArray(tags)) {
    throw new Error('Cannot save empty tags');
  }
  const {
    adAccountId,
    organizationId,
    publisherId,
    businessProfileId,
  } = currentUserContext;

  // add the user context when saving
  const tagsToSave = tags.map((tag) => {
    const tagObj = { ...tag };
    if (isNotNil(organizationId)) {
      tagObj.organization_id = organizationId;
    }
    if (isNotNil(adAccountId)) {
      tagObj.ad_account_id = adAccountId;
    }
    if (isNotNil(publisherId)) {
      tagObj.publisher_id = publisherId;
    }
    if (isNotNil(businessProfileId)) {
      tagObj.business_profile_id = businessProfileId;
    }

    return tagObj;
  });
  return apiWorkerService.post('/api/v1/tags', { tags: tagsToSave });
});

export const updateTags = wrapAPICall((currentUserContext, tags) => {
  if (isEmptyArray(tags)) {
    throw new Error('Cannot update empty tags');
  }
  return apiWorkerService.put('/api/v1/tags', { tags });
});

export const deleteTags = wrapAPICall((tagIds) => {
  return apiWorkerService.delete(`/api/v1/tags?ids=${tagIds.join(',')}`);
});

export const getTagsByTagType = wrapAPICall((tagType) => {
  if (!VALID_TEMPLATE_TAG_TYPES.has(tagType)) {
    throw new TAG_ERRORS.InvalidTagType(tagType);
  }

  return apiWorkerService.get(`/api/v1/tags/type/${tagType}`);
});

export const saveTagsForTemplate = wrapAPICall((templateId, tagIds) => {
  if (isNil(templateId)) {
    throw new Error('Cannot add template tags with empty Template ID.');
  }
  if (isEmptyArray(tagIds)) {
    throw new Error('Cannot add template tags with empty Tag IDs.');
  }
  return apiWorkerService.post(
    `/api/v1/templates/${templateId}/tags/?tagIds=${tagIds.join(',')}`,
  );
});

export const deleteTagsForTemplate = wrapAPICall((templateId, tagIds) => {
  if (isNil(templateId)) {
    throw new Error('Cannot delete template tags with empty Template ID.');
  }
  if (isEmptyArray(tagIds)) {
    throw new Error('Cannot delete template tags with empty Tag IDs.');
  }
  return apiWorkerService.delete(
    `/api/v1/templates/${templateId}/tags/?tagIds=${tagIds.join(',')}`,
  );
});

export const getUserCreativeFields = wrapAPICall(() => {
  return apiWorkerService.get('/api/v1/me/creativeFields/');
});

export const getUserFeatureFlags = DEV
  ? getUserFeatureFlagsFixture
  : wrapAPICall(() => {
      return apiWorkerService.get('/api/v1/me/featureFlags/');
    });

/**
 * @param {CreativeContentWireObject} creativeContents
 * @type {Promise<APIResponse>}
 */
export const saveCreativeContents = wrapAPICall((creativeContents) => {
  if (
    !CreativeContentWireObject.isCreativeContentWireObject(creativeContents)
  ) {
    throw new Error('creativeContents must be CreativeContentWireObject');
  }

  const requestData = {
    creativeContents: [creativeContents.toJSON()],
  };

  const { id } = creativeContents;
  if (isEmptyString(id)) {
    return apiWorkerService.post('/api/v1/creativeContents/', requestData);
  }

  return apiWorkerService.put('/api/v1/creativeContents/', requestData);
});

export const getCreativeContents = wrapAPICall((creativeContentId) => {
  return apiWorkerService.get(`/api/v1/creativeContents/${creativeContentId}`);
});

export const populateCreativeContents = wrapAPICall(
  (templateIds, creativeContentId, url) => {
    if (!isArray(templateIds)) {
      throw new Error('templateIds should be an array.');
    }

    const creativeContentsPopulateRequest = {
      creativeContentPopulateRequests: [
        {
          creative_content_id: creativeContentId,
          url,
          template_ids: templateIds,
        },
      ],
    };
    return apiWorkerService.post(
      '/api/v1/creativeContents/populate',
      creativeContentsPopulateRequest,
    );
  },
);

export const generateCreativeFromTemplate = wrapAPICall(
  (templateId, creativeContentId, creativeConfig) => {
    return apiWorkerService.post(
      `/api/v1/creatives/from-template/${templateId}/from-content/${creativeContentId}`,
      creativeConfig,
    );
  },
);

export const createEditionForCreative = DEV
  ? createEditionForCreativeFixture
  : wrapAPICall((creativeId, numberOfNewSnaps, title) => {
      if (isNil(creativeId)) {
        throw new Error('Cannot create edition without creativeId');
      }
      if (isNotNumber(numberOfNewSnaps)) {
        throw new Error('numberOfNewSnaps should be a number');
      }
      const basePath = `/api/v1/creatives/${creativeId}/editions`;
      const params = {
        numberOfNewSnaps,
      };
      if (isNotEmptyString(title)) {
        params.title = title;
      }
      return apiWorkerService.post(appendQueryParams(basePath, params));
    });

export const addSnapsToStoryForCreative = wrapAPICall(
  (creativeId, storyId, numberOfNewSnaps) => {
    if (isNil(creativeId)) {
      throw new Error('Cannot add snaps to story without creativeId');
    }
    if (isNil(storyId)) {
      throw new Error('Cannot add snaps to story without storyId');
    }
    if (isNotNumber(numberOfNewSnaps)) {
      throw new Error('numberOfNewSnaps should be a number');
    }
    const basePath = `/api/v1/creatives/${creativeId}/add_snaps`;
    const params = {
      numberOfNewSnaps,
      storyId,
    };
    return apiWorkerService.post(appendQueryParams(basePath, params));
  },
);

export const updateRichSnapForCreative = DEV
  ? updateRichSnapForCreativeFixture
  : wrapAPICall(
      (
        creativeId,
        versionId,
        outputMediaId,
        storyStudioSnapId,
        storyStudioStoryId,
      ) => {
        if (isNil(creativeId)) {
          throw new Error('Cannot update rich snap without creativeId');
        }
        if (isNil(versionId)) {
          throw new Error('Cannot update rich snap without versionId');
        }
        if (isNil(outputMediaId)) {
          throw new Error('Cannot update rich snap without outputMediaId');
        }
        if (isNil(storyStudioSnapId)) {
          throw new Error('Cannot update rich snap without storyStudioSnapId');
        }
        const basePath = `/api/v1/creatives/${creativeId}/versions/${versionId}/update_rich_snap`;
        const params = {
          outputMediaId,
          snapId: storyStudioSnapId,
          storyId: storyStudioStoryId,
        };
        return apiWorkerService.post(appendQueryParams(basePath, params));
      },
    );

export const updateDiscoverStoryVideoTrack = wrapAPICall(
  (
    creativeId,
    versionId,
    outputMediaId,
    storyStudioStoryId,
    chaptersWithShots,
  ) => {
    if (isNil(creativeId)) {
      throw new Error('Cannot update discover story without creativeId');
    }
    if (isNil(versionId)) {
      throw new Error('Cannot update discover story without versionId');
    }
    if (isNil(outputMediaId)) {
      throw new Error('Cannot update discover story without outputMediaId');
    }
    const basePath = `/api/v1/creatives/${creativeId}/versions/${versionId}/update_story_videotrack`;
    const params = {
      outputMediaId,
      storyId: storyStudioStoryId,
    };
    const data = isNil(chaptersWithShots) ? {} : chaptersWithShots.toJSON();
    return apiWorkerService.post(appendQueryParams(basePath, params), data);
  },
);

export const createZippedCreativeVersion = DEV
  ? createZippedCreativeVersionFixture
  : wrapAPICall((creativeId, versionId) => {
      if (isNil(creativeId)) {
        throw new Error('Cannot create zipped file without creativeId');
      }
      if (isNil(versionId)) {
        throw new Error('Cannot create zipped file without versionId');
      }
      return apiWorkerService.post(
        `/api/v1/creatives/${creativeId}/versions/${versionId}/zip`,
      );
    });

export const checkZippedCreativeVersion = DEV
  ? createZippedCreativeVersionFixture
  : wrapAPICall((creativeId, versionId) => {
      if (isNil(creativeId)) {
        throw new Error('Cannot check zipped file without creativeId');
      }
      if (isNil(versionId)) {
        throw new Error('Cannot check zipped file without versionId');
      }
      return apiWorkerService.get(
        `/api/v1/creatives/${creativeId}/versions/${versionId}/zip`,
      );
    });

export function loadImageAsBlob(url) {
  return new Promise((resolve, reject) => {
    getArrayBufferFromURL(url)
      .then((databack) => {
        const { data: arrayBuffer } = databack;
        const imageBuffer = new Uint8Array(arrayBuffer);
        const blob = new Blob([imageBuffer], { type: 'image/png' });
        getLocalURIFromFile(blob).then((mediaUrl) => {
          resolve(mediaUrl);
        });
      })
      .catch((error) => {
        warn("Can't load image.", error);
        reject(error);
      });
  });
}

export const generateCreativeFromUserInput = wrapAPICall(
  (autoCreateRequest) => {
    return apiWorkerService.post('/api/v1/creatives/auto-create/user-input', {
      autoCreateRequests: [autoCreateRequest],
    });
  },
);
